import React, { useRef } from 'react';
import { Label } from '@/components/ui/label';
import { CalendarDays } from 'lucide-react';

/**
 * When the rep actually met this person.
 *
 * This is not the same as when the row was created. A card typed in three days
 * after a show was still met on the show day, and the follow up says "it was
 * great meeting you on Thursday", so getting it from the row's creation time
 * tells the prospect the wrong day.
 *
 * It writes to captured_at, which already means exactly this: every one of the
 * 78 imported HITEC contacts has a captured_at inside the show's own dates.
 *
 * Designed for a thumb on a phone. Typing a date on a mobile date picker is
 * slow, so the common answers are one tap: the days of the show being captured
 * into, or failing that today and the two days before.
 */

/** The calendar date in the reps' timezone, as YYYY-MM-DD. */
const TIMEZONE = 'America/Chicago';

export function localDate(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // en-CA formats as YYYY-MM-DD, which is what a date input wants.
  return new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE }).format(date);
}

/**
 * A date input gives back YYYY-MM-DD. Store it as midday UTC.
 *
 * Midday rather than midnight so that reading the date back in any US timezone
 * lands on the same calendar day. Midnight UTC is the previous evening in
 * Chicago, which would quietly shift every date by one.
 */
export function dateToIso(dateString) {
  return dateString ? `${dateString}T12:00:00.000Z` : null;
}

function addDays(dateString, delta) {
  const [y, m, d] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

function labelFor(dateString, today) {
  if (dateString === today) return 'Today';
  if (dateString === addDays(today, -1)) return 'Yesterday';

  const [y, m, d] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));

  // Built rather than formatted in one call. Asking Intl for weekday and day
  // together gives "16 Tue" here, and the order is not something to rely on:
  // it varies with the environment's locale data.
  const weekday = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  }).format(date);

  return `${weekday} ${d}`;
}

/**
 * The readable "Met on Tue, Sep 23" text, distinct from labelFor's short
 * quick-pick button labels (which special-case Today/Yesterday): this is the
 * tap target for the real picker below, so it always shows the actual date,
 * even when that date is today or yesterday.
 */
function formatMetOnDate(dateString) {
  const [y, m, d] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * The one tap options.
 *
 * A show's own days when there is a real show with a sane range, otherwise the
 * last three days. The range guard matters: the personal "All other times"
 * bucket runs from 2026-06-15 to 2027-12-31, and without it that would try to
 * render five hundred buttons.
 */
function quickDates(show, today) {
  const start = show?.start_date;
  const end = show?.end_date;

  if (show && !show.is_general && start && end && start <= end) {
    const days = [];
    for (let day = start; day <= end && days.length <= 31; day = addDays(day, 1)) {
      days.push(day);
    }
    // Never offer a day that has not happened yet.
    const past = days.filter((day) => day <= today);
    if (past.length > 0 && days.length <= 14) {
      // Most recent last, and never more than six buttons on a phone.
      return past.slice(-6);
    }
  }

  return [addDays(today, -2), addDays(today, -1), today];
}

export default function MetOnField({ value, onChange, show }) {
  const today = localDate();
  const selected = value ? localDate(value) : today;
  const options = quickDates(show, today);
  const dateInputRef = useRef(null);

  const usingShowDays =
    show && !show.is_general && show.start_date && show.end_date && options[0] !== addDays(today, -2);

  /**
   * Opens the real native date picker (task: make the date text itself the
   * tap target, 2026-09-23). Before this, the only way in was the browser's
   * own tiny calendar-picker-indicator icon on the date input, which on
   * desktop Chrome and some embedded webviews is a small icon at the far
   * right of the box rather than the input as a whole.
   *
   * showPicker() is the direct way to ask for it, but it is not supported
   * everywhere (older WebKit in particular), and even where it exists it can
   * still throw (a "not being rendered" edge case in some embedded
   * webviews). Falling back to focus()+click() on failure or absence covers
   * that: clicking a date input has opened its picker since long before
   * showPicker() existed, so this can never leave a rep with no way in.
   */
  const openPicker = () => {
    const el = dateInputRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        // Fall through to the older, always-available trigger below.
      }
    }
    el.focus();
    el.click();
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Met On
      </Label>

      <div className="flex flex-wrap gap-1.5">
        {options.map((day) => {
          const isSelected = day === selected;
          return (
            <button
              key={day}
              type="button"
              onClick={() => onChange(dateToIso(day))}
              className={`px-3 h-9 rounded-lg text-xs font-semibold border transition-colors ${
                isSelected
                  ? 'bg-accent text-accent-foreground border-accent'
                  : 'bg-secondary/50 text-foreground border-transparent hover:bg-secondary'
              }`}
            >
              {labelFor(day, today)}
            </button>
          );
        })}
      </div>

      {/* The date text itself is the primary tap target now (task: make the
          date text itself clickable, 2026-09-23), not the small calendar
          icon a real <input type="date"> shows on its own: this whole row
          is one large button that opens the same native picker via
          openPicker() above. The real <input> stays in the DOM (sr-only,
          not display:none/visibility:hidden, both of which would make
          showPicker() throw): it is what actually gives the OS's own
          picker, and its min/max validation, for free. */}
      <button
        type="button"
        onClick={openPicker}
        className="w-full flex items-center gap-2 h-11 px-3 rounded-lg bg-secondary/50 text-sm font-medium text-foreground text-left"
      >
        <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" />
        Met on {formatMetOnDate(selected)}
      </button>
      <input
        ref={dateInputRef}
        type="date"
        value={selected}
        max={today}
        onChange={(e) => {
          // The picker is capped at today, but a typed value can still be in
          // the future in some browsers. The server refuses those too.
          if (e.target.value && e.target.value <= today) onChange(dateToIso(e.target.value));
        }}
        tabIndex={-1}
        className="sr-only"
      />

      <p className="text-[10px] text-muted-foreground/70">
        {usingShowDays
          ? `Days of ${show.name}. This sets the day named in the text and email.`
          : 'This sets the day named in the text and email. Future dates are not allowed.'}
      </p>
    </div>
  );
}
